import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './Inventory.css';

function Inventory({ tenantId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    quantity: 0,
    unit: 'Stück',
    locationId: '',
    purchasePrice: 0,
    sellingPrice: 0,
    currentValue: 0,
    partNumber: ''
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadInventory();
      loadLocations();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadInventory = async () => {
    try {
      const data = await api.get('/inventory', tenantId);
      setItems(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await api.get('/locations', tenantId);
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : '-';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, formData, tenantId);
      } else {
        await api.post('/inventory', formData, tenantId);
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        category: '',
        quantity: 0,
        unit: 'Stück',
        locationId: '',
        purchasePrice: 0,
        sellingPrice: 0,
        currentValue: 0,
        partNumber: ''
      });
      loadInventory();
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Inventar-Item wirklich löschen?')) {
      try {
        await api.delete(`/inventory/${id}`, tenantId);
        loadInventory();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const totalValue = items.reduce((sum, item) => sum + (item.currentValue || 0) * (item.quantity || 0), 0);

  return (
    <div className="inventory">
      <div className="page-header">
        <h1>Inventar</h1>
        <div>
          <span style={{ marginRight: '1rem', color: '#666' }}>
            Gesamtwert: <strong>{totalValue.toFixed(2)} {currency}</strong>
          </span>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
            Neues Inventar-Item
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingItem ? 'Inventar-Item bearbeiten' : 'Neues Inventar-Item'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Teilenummer</label>
                  <input
                    type="text"
                    value={formData.partNumber}
                    onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Kategorie</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Werkstattort</label>
                  <select
                    value={formData.locationId}
                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                  >
                    <option value="">Kein Ort</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Menge</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, quantity: val });
                    }}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Einheit</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Einkaufspreis ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice}
                    onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, purchasePrice: val });
                    }}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Verkaufspreis ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.sellingPrice}
                    onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, sellingPrice: val });
                    }}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Aktueller Wert ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.currentValue}
                    onChange={(e) => setFormData({ ...formData, currentValue: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, currentValue: val });
                    }}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingItem ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={items}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || '',
            render: (row) => (
              <button 
                className="link-button" 
                onClick={() => navigate(`/inventory/${row.id}`)}
              >
                {row.name || ''}
              </button>
            )
          },
          {
            key: 'partNumber',
            header: 'Teilenummer',
            sortType: 'string',
            accessor: (row) => row.partNumber || '-',
            render: (row) => {
              if (!row.partNumber) return '-';
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{row.partNumber}</span>
                  <a
                    href={`https://www.autodoc.de/search?keyword=${encodeURIComponent(row.partNumber)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontSize: '0.85em', color: '#2196f3', textDecoration: 'none' }}
                    title="Auf AutoDoc suchen"
                  >
                    🔍
                  </a>
                  <a
                    href={`https://www.ebay.de/sch/131090/i.html?_nkw=${encodeURIComponent(row.partNumber)}&_from=R40`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ fontSize: '0.85em', color: '#2196f3', textDecoration: 'none' }}
                    title="Auf eBay suchen"
                  >
                    🛒
                  </a>
                </div>
              );
            }
          },
          {
            key: 'category',
            header: 'Kategorie',
            sortType: 'string',
            accessor: (row) => row.category || ''
          },
          {
            key: 'quantity',
            header: 'Menge',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.quantity || 0,
            render: (row) => `${row.quantity} ${row.unit || 'Stück'}`
          },
          {
            key: 'locationId',
            header: 'Werkstattort',
            sortType: 'string',
            accessor: (row) => getLocationName(row.locationId)
          },
          {
            key: 'purchasePrice',
            header: 'Einkaufspreis',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.purchasePrice || 0,
            render: (row) => `${row.purchasePrice?.toFixed(2)} ${currency}`
          },
          {
            key: 'sellingPrice',
            header: 'Verkaufspreis',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.sellingPrice || 0,
            render: (row) => `${row.sellingPrice?.toFixed(2)} ${currency}`
          },
          {
            key: 'currentValue',
            header: 'Aktueller Wert',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.currentValue || 0,
            render: (row) => `${row.currentValue?.toFixed(2)} ${currency}`
          },
          {
            key: 'totalValue',
            header: 'Gesamtwert',
            sortType: 'number',
            align: 'right',
            accessor: (row) => (row.currentValue || 0) * (row.quantity || 0),
            render: (row) => {
              const itemValue = (row.currentValue || 0) * (row.quantity || 0);
              return <strong>{itemValue.toFixed(2)} {currency}</strong>;
            }
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Inventargegenstände vorhanden"
      />
    </div>
  );
}

export default Inventory;

